/**
 * List Sync
 *
 * Syncs collection list data to static lists by sync-id.
 *
 * Simple mode (text/href):
 *   <div data-hs-sync="source" data-hs-sync-id="services">
 *     <a data-hs-sync="item" href="/service-1">Service 1</a>
 *   </div>
 *   <div data-hs-sync="target" data-hs-sync-id="services">
 *     <a data-hs-sync="item" href="#">Placeholder</a>
 *   </div>
 *
 * Field-based mode (named fields):
 *   <div data-hs-sync="source" data-hs-sync-id="projects">
 *     <div data-hs-sync="item">
 *       <img data-hs-sync-field="before" src="/before.jpg">
 *       <img data-hs-sync-field="after" src="/after.jpg">
 *     </div>
 *   </div>
 *   <div data-hs-sync="target" data-hs-sync-id="projects">
 *     <div data-hs-sync="item">
 *       <img data-hs-sync-field="before" src="/placeholder.jpg">
 *       <img data-hs-sync-field="after" src="/placeholder.jpg">
 *     </div>
 *   </div>
 *
 * Value-based matching (match by value):
 *   <div data-hs-sync="source" data-hs-sync-field="area-type">
 *     <div data-hs-sync="item" data-hs-sync-value="Residential">
 *       <svg>...</svg>
 *     </div>
 *     <div data-hs-sync="item" data-hs-sync-value="Commercial">
 *       <svg>...</svg>
 *     </div>
 *   </div>
 *   <div data-hs-sync="target" data-hs-sync-field="area-type" data-hs-sync-value="Residential">
 *     <div data-hs-sync="item"><!-- Matched SVG injected here --></div>
 *   </div>
 *
 * Child mode (wrapper templates):
 *   <div data-hs-sync="target" data-hs-sync-id="marquee">
 *     <div data-hs-sync="item" data-hs-sync-mode="child">
 *       <div class="marquee_list">
 *         <img data-hs-sync-field="image" src="/placeholder.jpg">
 *       </div>
 *     </div>
 *   </div>
 *
 * Supports data-hs-sync="ignore" to preserve elements during sync.
 */

import { querySelectorAll, querySelector } from '@utils';

/**
 * Get all text nodes within an element
 */
function getTextNodes(element: Element): Text[] {
  const textNodes: Text[] = [];
  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, null);

  let node: Node | null;
  while ((node = walker.nextNode())) {
    // Skip empty/whitespace-only nodes
    if (node.nodeValue?.trim()) {
      textNodes.push(node as Text);
    }
  }

  return textNodes;
}

/**
 * Get all elements with href attribute (including the element itself)
 */
function getHrefElements(element: Element): Element[] {
  const hrefs: Element[] = [];

  // Check if element itself has href
  if (element.hasAttribute('href')) {
    hrefs.push(element);
  }

  // Check descendants
  hrefs.push(...Array.from(element.querySelectorAll('[href]')));

  return hrefs;
}

/**
 * Check if item uses field-based syncing
 */
function usesFieldBasedSync(
  item: Element,
  config: { attributes: { properties: { syncField: string } } }
): boolean {
  const fieldAttr = config.attributes.properties.syncField;
  return item.querySelector(`[${fieldAttr}]`) !== null;
}

/**
 * Find element with src attribute (check element itself, then descendants)
 */
function findElementWithSrc(element: Element): Element | null {
  // Check element itself
  if (element.hasAttribute('src')) {
    const src = element.getAttribute('src');
    if (src) return element;
  }

  // Check descendants
  const descendant = element.querySelector('[src]');
  if (descendant) {
    const src = descendant.getAttribute('src');
    if (src) return descendant;
  }

  return null;
}

/**
 * Sync data using field-based mapping
 * Maps fields by name between source and target
 */
function syncFieldBasedData(
  sourceItem: Element,
  targetClone: Element,
  config: { attributes: { properties: { syncField: string } } }
): void {
  const fieldAttr = config.attributes.properties.syncField;

  // Get all fields in source
  const sourceFields = sourceItem.querySelectorAll(`[${fieldAttr}]`);

  sourceFields.forEach((sourceField: Element) => {
    const fieldName = sourceField.getAttribute(fieldAttr);

    // Find matching field in target
    const targetField = targetClone.querySelector(`[${fieldAttr}="${fieldName}"]`);
    if (!targetField) return;

    // Try to find elements with src (for images)
    const sourceImgElement = findElementWithSrc(sourceField);
    const targetImgElement = findElementWithSrc(targetField);

    if (sourceImgElement && targetImgElement) {
      // Sync src attribute
      const src = sourceImgElement.getAttribute('src');
      if (src) {
        targetImgElement.setAttribute('src', src);
      }
    }
    // Link elements: sync href
    else if (sourceField.hasAttribute('href') && targetField.hasAttribute('href')) {
      const href = sourceField.getAttribute('href');
      if (href) {
        targetField.setAttribute('href', href);
      }
    }
    // Text elements: sync text content
    else {
      const sourceTextNodes = getTextNodes(sourceField);
      const sourceText =
        sourceTextNodes.length > 0 ? sourceTextNodes[0].nodeValue : sourceField.textContent.trim();

      if (sourceText) {
        const targetTextNodes = getTextNodes(targetField);
        if (targetTextNodes.length > 0) {
          targetTextNodes[0].nodeValue = sourceText;
        } else {
          targetField.textContent = sourceText;
        }
      }
    }
  });
}

/**
 * Sync data from source item to target clone
 * Extracts text and href from source, applies to ALL matching elements in target
 */
function syncItemData(
  sourceItem: Element,
  targetClone: Element,
  config: { attributes: { properties: { syncField: string } } }
): void {
  // Check if using field-based syncing
  if (usesFieldBasedSync(targetClone, config)) {
    syncFieldBasedData(sourceItem, targetClone, config);
    return;
  }

  // Fallback to simple syncing (existing behavior)
  // Get text content from source (first non-empty text node)
  const sourceTextNodes = getTextNodes(sourceItem);
  const sourceText = sourceTextNodes.length > 0 ? sourceTextNodes[0].nodeValue : '';

  // Get href from source (first href element)
  const sourceHrefs = getHrefElements(sourceItem);
  const sourceHref = sourceHrefs.length > 0 ? sourceHrefs[0].getAttribute('href') : '';

  // Apply source text to ALL text nodes in target
  if (sourceText) {
    const targetTextNodes = getTextNodes(targetClone);
    targetTextNodes.forEach((node) => {
      node.nodeValue = sourceText;
    });
  }

  // Apply source href to ALL href elements in target
  if (sourceHref) {
    const targetHrefs = getHrefElements(targetClone);
    targetHrefs.forEach((el) => {
      el.setAttribute('href', sourceHref);
    });
  }
}

interface SyncConfig {
  attributes: {
    elements: {
      item: { primary: string };
    };
    properties: {
      syncId: string;
      syncField: string;
      syncMode: string;
      syncValue: string;
    };
  };
}

export async function init(config: SyncConfig): Promise<{ result: string; destroy: () => void }> {
  // Find all source lists and build maps by sync-id and sync-field
  const sourceLists = querySelectorAll(config, 'source');
  const sourceMapById = new Map<string, Element>();
  const sourceMapByField = new Map<string, Element>();

  sourceLists.forEach((source) => {
    const syncId = source.getAttribute(config.attributes.properties.syncId);
    const syncField = source.getAttribute(config.attributes.properties.syncField);

    if (syncId) {
      sourceMapById.set(syncId, source);
    }
    if (syncField) {
      sourceMapByField.set(syncField, source);
    }
  });

  // Find all target lists
  const targetLists = querySelectorAll(config, 'target');
  let syncedCount = 0;
  let skippedCount = 0;

  targetLists.forEach((target) => {
    const syncId = target.getAttribute(config.attributes.properties.syncId);
    const syncField = target.getAttribute(config.attributes.properties.syncField);
    const syncValue = target.getAttribute(config.attributes.properties.syncValue);

    // Determine which matching mode to use
    let source: Element | undefined;

    // Value-based matching (sync-field + sync-value)
    if (syncField && syncValue) {
      source = sourceMapByField.get(syncField);
      if (!source) {
        skippedCount++;
        return;
      }
    }
    // ID-based matching (sync-id)
    else if (syncId) {
      source = sourceMapById.get(syncId);
      if (!source) {
        skippedCount++;
        return;
      }
    }
    // No valid matching criteria
    else {
      skippedCount++;
      return;
    }

    // Find item template in target (element to clone)
    const targetTemplateWrapper = querySelector(config, 'item', target);
    if (!targetTemplateWrapper) {
      const identifier = syncField || syncId || 'unknown';
      console.warn(`[sync] Target list "${identifier}" missing item template`);
      return;
    }

    // Check sync mode - determines which element to use as template
    const syncMode = targetTemplateWrapper.getAttribute(config.attributes.properties.syncMode);
    const isChildMode = syncMode === 'child';

    // Find ignore element in target (if exists)
    const ignoreElement = querySelector(config, 'ignore', target);

    // Find source items - filtered by value if value-based matching
    let sourceItems: Element[];

    if (syncValue) {
      // Value-based matching: find single item with matching sync-value
      const allSourceItems = querySelectorAll(config, 'item', source);
      const matchedItem = Array.from(allSourceItems).find((item) => {
        const itemValue = item.getAttribute(config.attributes.properties.syncValue);
        return itemValue === syncValue;
      });

      if (!matchedItem) {
        console.warn(
          `[sync] No source item found with value "${syncValue}" in field "${syncField}"`
        );
        return;
      }

      sourceItems = [matchedItem];
    } else {
      // ID-based matching: use all source items
      sourceItems = Array.from(querySelectorAll(config, 'item', source));
      if (sourceItems.length === 0) {
        console.warn(`[sync] Source list "${syncId}" has no items`);
        return;
      }
    }

    // Determine template based on mode
    let template: Element;
    if (isChildMode) {
      // Child mode: clone the wrapper, we'll replace its child content
      const firstChild = targetTemplateWrapper.firstElementChild;
      if (!firstChild) {
        console.warn(
          `[sync] Target list "${syncId}" has sync-mode="child" but item wrapper has no children`
        );
        return;
      }
      // Clone the child element to use for syncing data
      template = firstChild.cloneNode(true) as Element;
    } else {
      // Default mode: clone the item element itself
      template = targetTemplateWrapper.cloneNode(true) as Element;
      template.removeAttribute(config.attributes.elements.item.primary.split('=')[0]);
    }

    // Determine insertion strategy if ignore exists
    let insertAfterIgnore = false;
    if (ignoreElement) {
      // Check if template wrapper comes after ignore element
      let currentElement = ignoreElement.nextElementSibling;
      while (currentElement) {
        if (currentElement === targetTemplateWrapper) {
          insertAfterIgnore = true;
          break;
        }
        currentElement = currentElement.nextElementSibling;
      }
    }

    if (isChildMode) {
      // CHILD MODE: Keep wrapper as single element, duplicate children inside it
      // Clear the wrapper's children
      while (targetTemplateWrapper.firstChild) {
        targetTemplateWrapper.removeChild(targetTemplateWrapper.firstChild);
      }

      // Create synced children and append to the wrapper
      sourceItems.forEach((sourceItem) => {
        const childClone = template.cloneNode(true) as Element;
        syncItemData(sourceItem, childClone, config);
        targetTemplateWrapper.appendChild(childClone);
      });

      // Remove data-hs-sync="item" attribute from wrapper
      targetTemplateWrapper.removeAttribute(config.attributes.elements.item.primary.split('=')[0]);
    } else {
      // DEFAULT MODE: Duplicate the item element itself
      // Clear target list, but preserve ignore element
      const children = Array.from(target.children);
      children.forEach((child) => {
        if (child !== ignoreElement) {
          child.remove();
        }
      });

      // Create synced items
      const syncedItems = Array.from(sourceItems).map((sourceItem) => {
        const clone = template.cloneNode(true) as Element;
        syncItemData(sourceItem, clone, config);
        return clone;
      });

      // Insert synced items based on strategy
      if (ignoreElement) {
        if (insertAfterIgnore) {
          // Insert all items after ignore
          syncedItems.forEach((item) => {
            target.appendChild(item);
          });
        } else {
          // Insert all items before ignore
          syncedItems.forEach((item) => {
            target.insertBefore(item, ignoreElement);
          });
        }
      } else {
        // No ignore element, just append all
        syncedItems.forEach((item) => {
          target.appendChild(item);
        });
      }
    }

    syncedCount++;
  });

  return {
    result: `sync initialized (${syncedCount} synced, ${skippedCount} skipped)`,
    destroy: () => {},
  };
}
